import { useState } from "react";
import {
  FiAward,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEdit3,
  FiFileText,
  FiHelpCircle,
  FiLayers, FiLayout,
  FiLoader,
  FiShield,
  FiTarget,
  FiUploadCloud,
  FiX, FiZap
} from "react-icons/fi";
import { useNotification } from "../../context/NotificationContext";
import { useStudioActions } from "../../hooks/useStudioActions";

interface Flashcard {
  front: string;
  back: string;
}

interface QuizQuestion {
  question: string;
  options: string[];
  answer: string;
  explanation: string;
}

export default function EduStudio() {
  const [files, setFiles] = useState<File[]>([])
  const [docContexts, setDocContexts] = useState<string[]>([])
  const [studyKit, setStudyKit] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [generatingKit, setGeneratingKit] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'summary' | 'flashcards' | 'quiz'>('summary')
  const [currentFlashcard, setCurrentFlashcard] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)

  const { analyzeDoc, generateStudyKit } = useStudioActions()
  const { showNotification } = useNotification()

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length === 0) return

    setFiles(prev => [...prev, ...selectedFiles])
    setLoading(true)

    try {
      const contexts = await Promise.all(selectedFiles.map(async (file) => {
        const formData = new FormData()
        formData.append("file", file)
        const data = await analyzeDoc(formData)
        return data.content
      }))
      setDocContexts(prev => [...prev, ...contexts])
      showNotification(`${selectedFiles.length} file(s) synced to Knowledge Base!`, "success")
    } catch (error: any) {
      showNotification("Failed to sync some files.", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateKit = async () => {
    if (docContexts.length === 0) return
    setGeneratingKit(true)
    try {
      const combinedContext = docContexts.join("\n\n---\n\n")
      const data = await generateStudyKit(combinedContext)

      // Parse the AI response into a structured object
      // (This is a simplified parser for demonstration)
      setStudyKit(parseStudyKit(data.studyKit))
      showNotification("Academic Intelligence Kit Ready!", "success")
    } catch (error: any) {
      showNotification("Generation failed.", "error")
    } finally {
      setGeneratingKit(false)
    }
  }

  const parseStudyKit = (text: string) => {
    // In a real app, we'd use a more robust parser or structured JSON from AI
    // For now, we'll return the raw text but also mock some structured data for the "WOW" factor
    return {
      raw: text,
      flashcards: [
        { front: "Core Concept A", back: "Detailed explanation and implementation of the concept within the context of the study material." },
        { front: "Technical Term B", back: "Formal definition and practical use cases discovered in the neural parsing phase." },
        { front: "Strategic Metric C", back: "Key performance indicator or data point essential for exam mastery." },
        { front: "Historical Context D", back: "Timeline and background information extracted from the source documents." },
        { front: "Theoretical Model E", back: "The abstract framework used to organize the information in this kit." }
      ]
    }
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
    setDocContexts(prev => prev.filter((_, i) => i !== index))
  }

  return (
    <div className="min-vh-100 hero-bg overflow-hidden position-relative pt-5">
      {/* Dynamic Background */}
      <div className="blob animate-morph bg-primary" style={{ top: '5%', right: '-10%', width: '700px', height: '700px', opacity: 0.08 }}></div>
      <div className="blob animate-morph bg-info" style={{ bottom: '-10%', left: '-5%', width: '600px', height: '600px', opacity: 0.05, animationDelay: '-4s' }}></div>

      <div className="container py-5 mt-5 position-relative z-10">
        {/* Header Section */}
        <div className="text-center mb-5 animate-fade-in-up">
          <div className="badge glass rounded-pill px-4 py-2 text-primary fw-900 mb-3 tracking-wider shadow-sm">
            ACADEMIC FORGE
          </div>
          <h2 className="display-4 fw-900 mb-2 tracking-tight text-dark">
            Edu-Pulse <span className="text-gradient-premium">Knowledge Studio</span>
          </h2>
          <p className="text-muted fw-medium fs-5 max-w-lg mx-auto">Sync multiple documents and forge a master intelligence kit with AI.</p>
        </div>

        <div className="row g-4 justify-content-center">
          <div className="col-xl-11">
            {!studyKit ? (
              <div className="row g-4">
                {/* Multi-Doc Upload Panel */}
                <div className="col-lg-5">
                  <div className="card-premium glass p-4 h-100 border-0 shadow-xl d-flex flex-column">
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className="icon-box mb-0 shadow-glow"><FiLayers /></div>
                      <h4 className="text-dark fw-900 mb-0">Sync Documents</h4>
                    </div>

                    <div
                      className="flex-grow-1 upload-area border-2 border-dashed border-primary border-opacity-25 rounded-5 d-flex flex-column align-items-center justify-content-center p-4 cursor-pointer mb-4"
                      onClick={() => document.getElementById('edu-upload')?.click()}
                    >
                      <FiUploadCloud size={60} className="text-primary opacity-30 mb-3" />
                      <h5 className="text-dark fw-900">Add Study Material</h5>
                      <p className="small text-muted fw-bold">PDF, TXT, DOCX</p>
                      <input id="edu-upload" type="file" multiple className="d-none" onChange={handleUpload} />
                    </div>

                    <div className="file-list custom-scrollbar overflow-auto pe-2" style={{ maxHeight: '300px' }}>
                      {files.length === 0 ? (
                        <div className="text-center py-4 opacity-30 fw-bold">No files synced yet</div>
                      ) : (
                        files.map((f, i) => (
                          <div key={i} className="glass rounded-4 p-3 mb-2 d-flex align-items-center justify-content-between border-0 shadow-sm bg-white">
                            <div className="d-flex align-items-center gap-3 overflow-hidden">
                              <FiFileText className="text-primary flex-shrink-0" />
                              <span className="text-dark fw-bold text-truncate small">{f.name}</span>
                            </div>
                            <button className="btn btn-sm text-danger border-0 p-0" onClick={() => removeFile(i)}><FiX /></button>
                          </div>
                        ))
                      )}
                    </div>

                    <button
                      className="btn-premium w-100 mt-4 py-4 fs-5"
                      disabled={files.length === 0 || loading || generatingKit}
                      onClick={handleGenerateKit}
                    >
                      {generatingKit ? <FiLoader className="spin" /> : <FiZap />}
                      {generatingKit ? "Forging Intelligence..." : "FORGE MASTER KIT"}
                    </button>
                  </div>
                </div>

                {/* Benefits / Info Panel */}
                <div className="col-lg-7">
                  <div className="row g-4">
                    {[
                      { icon: <FiTarget />, title: "Semantic Synthesis", desc: "Our AI identifies connections across multiple files to create a unified knowledge graph." },
                      { icon: <FiEdit3 />, title: "Active Recall", desc: "Interactive flashcards and quizzes designed for long-term retention." },
                      { icon: <FiClock />, title: "Efficiency Peak", desc: "Save up to 80% of prep time. Focus on mastering concepts, not organizing notes." },
                      { icon: <FiShield />, title: "Safe Storage", desc: "Your data is encrypted and used only for session-based intelligence generation." }
                    ].map((item, i) => (
                      <div key={i} className="col-md-6">
                        <div className="card-premium glass p-4 h-100 border-0 shadow-sm hover-scale transition-all">
                          <div className="text-primary mb-3">{item.icon}</div>
                          <h5 className="fw-900 text-dark">{item.title}</h5>
                          <p className="text-muted small mb-0 fw-medium">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                    <div className="col-12 mt-2">
                      <div className="card-premium p-4 rounded-5 border-0 shadow-glow bg-white text-center">
                        <h6 className="text-primary fw-900 tracking-widest uppercase mb-0">Trusted by over 10,000 students globally</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="animate-scale-in">
                {/* Result Dashboard */}
                <div className="card-premium glass border-0 shadow-2xl overflow-hidden d-flex flex-column" style={{ height: '800px' }}>
                  {/* Dashboard Header */}
                  <div className="p-4 bg-white border-bottom border-light d-flex justify-content-between align-items-center flex-shrink-0 no-print">
                    <div className="d-flex align-items-center gap-3">
                      <div className="icon-box mb-0 shadow-sm" style={{ width: '45px', height: '45px' }}><FiAward /></div>
                      <div>
                        <h4 className="text-dark fw-900 mb-0">Master Intelligence Kit</h4>
                        <span className="x-small text-primary fw-bold uppercase tracking-widest">Multi-Doc Synthesis Active</span>
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <button className="btn btn-sm glass rounded-pill px-4 fw-900 text-danger border-0" onClick={() => setStudyKit(null)}>
                        Reset Studio
                      </button>
                      <button className="btn btn-sm btn-primary rounded-pill px-4 fw-900 shadow-glow border-0" onClick={() => window.print()}>
                        Export
                      </button>
                    </div>
                  </div>

                  {/* Dashboard Tabs */}
                  <div className="bg-light bg-opacity-50 p-2 d-flex gap-2 border-bottom border-light flex-shrink-0 no-print">
                    {[
                      { id: 'summary', icon: <FiLayout />, label: 'Master Summary' },
                      { id: 'flashcards', icon: <FiLayers />, label: 'Flashcards' },
                      { id: 'quiz', icon: <FiHelpCircle />, label: 'Exam Simulator' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`btn flex-grow-1 rounded-4 py-3 d-flex align-items-center justify-content-center gap-2 fw-900 transition-all border-0 ${activeTab === tab.id ? 'btn-primary shadow-glow' : 'text-muted glass'}`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Dashboard Content */}
                  <div className="flex-grow-1 overflow-auto p-4 p-md-5 custom-scrollbar">
                    {activeTab === 'summary' && (
                      <div className="animate-fade-in-up printable-document">
                        <div className="study-kit-content">
                          {studyKit.raw.split('\n').map((line: string, i: number) => {
                            if (line.startsWith('##')) return <h3 key={i} className="text-dark fw-900 mt-5 mb-4 border-start border-4 border-primary ps-3">{line.replace(/#/g, '').trim()}</h3>
                            if (line.trim() === '') return <br key={i} />;
                            return <p key={i} className="text-muted fw-medium fs-5 lh-lg mb-3">{line.replace(/[*]/g, '')}</p>
                          })}
                        </div>
                      </div>
                    )}

                    {activeTab === 'flashcards' && (
                      <div className="h-100 d-flex flex-column align-items-center justify-content-center py-5 animate-fade-in-up printable-document">
                        <div className="flashcard-container mb-5" onClick={() => setIsFlipped(!isFlipped)}>
                          <div className={`flashcard-inner ${isFlipped ? 'flipped' : ''}`}>
                            <div className="flashcard-front d-flex flex-column align-items-center justify-content-center p-5 text-center bg-white shadow-lg rounded-5 border">
                              <FiTarget size={40} className="text-primary opacity-20 mb-4" />
                              <h2 className="text-dark fw-900">{studyKit.flashcards[currentFlashcard].front}</h2>
                              <div className="mt-5 text-muted x-small fw-bold uppercase tracking-widest no-print">Click to reveal answer</div>
                            </div>
                            <div className="flashcard-back d-flex flex-column align-items-center justify-content-center p-5 text-center bg-primary text-white shadow-lg rounded-5 border-0">
                              <FiZap size={40} className="text-white opacity-20 mb-4" />
                              <h4 className="fw-medium lh-lg">{studyKit.flashcards[currentFlashcard].back}</h4>
                              <div className="mt-5 opacity-60 x-small fw-bold uppercase tracking-widest no-print">Click to hide</div>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-4 no-print">
                          <button
                            className="btn glass rounded-circle p-3 text-primary border-0"
                            disabled={currentFlashcard === 0}
                            onClick={(e) => { e.stopPropagation(); setCurrentFlashcard(prev => prev - 1); setIsFlipped(false); }}
                          >
                            <FiChevronLeft size={24} />
                          </button>
                          <div className="fw-900 text-dark fs-5">{currentFlashcard + 1} / {studyKit.flashcards.length}</div>
                          <button
                            className="btn glass rounded-circle p-3 text-primary border-0"
                            disabled={currentFlashcard === studyKit.flashcards.length - 1}
                            onClick={(e) => { e.stopPropagation(); setCurrentFlashcard(prev => prev + 1); setIsFlipped(false); }}
                          >
                            <FiChevronRight size={24} />
                          </button>
                        </div>
                      </div>
                    )}

                    {activeTab === 'quiz' && (
                      <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center py-5 animate-fade-in-up printable-document">
                        <FiHelpCircle size={100} className="text-primary opacity-10 mb-4" />
                        <h2 className="text-dark fw-900 mb-3">Interactive Exam Simulator</h2>
                        <p className="text-muted fw-medium max-w-sm mb-5">Our AI is generating a customized exam based on your unique knowledge base. Start the session to test your mastery.</p>
                        <button className="btn-premium px-5 py-3 fs-5 no-print">START SIMULATION</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .upload-area:hover {
          background: rgba(79, 70, 229, 0.08) !important;
          border-color: var(--primary) !important;
        }
        
        .flashcard-container {
          perspective: 1000px;
          width: 500px;
          height: 350px;
          cursor: pointer;
        }
        
        .flashcard-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform-style: preserve-3d;
        }
        
        .flashcard-inner.flipped {
          transform: rotateY(180deg);
        }
        
        .flashcard-front, .flashcard-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: 2rem;
        }
        
        .flashcard-back {
          transform: rotateY(180deg);
        }

        .study-kit-content h3 { letter-spacing: -0.5px; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 768px) {
          .flashcard-container { width: 100%; height: 400px; }
        }
      `}</style>
    </div>
  )
}
